'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTransition } from 'react';

type ClearMode = 'two-step' | 'immediate';

function isUsedCarsLandingPage(pathname: string) {
  return pathname.startsWith('/used-cars') && !pathname.includes('/search');
}

export function isPriceLandingPage(pathname: string) {
  return pathname.split('/').some(seg => seg.startsWith('under-'));
}

export function isProvinceLandingPage(pathname: string) {
  const segments = pathname.split('/').filter(Boolean);

  return (
    segments[0] === 'used-cars' &&
    segments[1] === 'provinces'
  );
}

function getLandingType(pathname: string) {
  const segments = pathname.split('/').filter(Boolean);

  if (segments[0] !== 'used-cars') return null;

  if (segments[1] === 'search') return null;
  if (segments[1] === 'provinces') return 'province';
  if (segments[1] === 'demo') return 'demo';
  if (segments[1]?.startsWith('under-')) return 'price';
  if (segments[1]?.endsWith('-bodytype')) return 'body';
  if (segments.length >= 2) return 'make-model';

  return null;
}

export function useLandingAwareClear() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [, startTransition] = useTransition();

  return (mode: ClearMode) => {
    startTransition(() => {
        const landingType = getLandingType(pathname);

        if (!landingType && !isProvinceLandingPage(pathname)) {
          return;
        }

        const rawParams = new URLSearchParams(searchParams);
        const params = new URLSearchParams(
          Array.from(rawParams).filter(([_, value]) => value !== '')
        );

        const isDemo = params.get('type') === 'demo';

        if (!isUsedCarsLandingPage(pathname) && !isProvinceLandingPage(pathname)) {
          return;
        }

        if (mode === 'immediate') {
          router.push(
            isDemo
              ? '/used-cars/demo/search'
              : '/used-cars/search'
          );
          return;
        }

        const hasActiveFilters =
          params.has('bodytype') ||
          params.has('colours') ||
          params.has('doors');

        if (hasActiveFilters) {
          return;
        }

        router.push(
          isDemo
            ? '/used-cars/demo/search'
            : '/used-cars/search'
        );
    });
  };
}

